import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Building2, DollarSign, X, Heart, RotateCcw, Zap, ExternalLink, Loader2, Check, Sparkles, ShoppingCart, Navigation } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { cachedGet, invalidateCache } from '../utils/apiCache';
import { track, EVENTS } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SWIPE_CACHE_TTL_MS = 90000; // 90s — instant tab-switch returns, fresh enough deck

/* ── Applied Popup ── */
const AppliedPopup = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.95] }}
    transition={{ duration: 1.4, times: [0, 0.15, 0.7, 1], ease: 'easeOut' }}
    className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
  >
    <div className="flex flex-col items-center gap-2 bg-white/80 dark:bg-[#1c1c1c]/80 rounded-3xl px-10 py-8 shadow-lg">
      <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
        <Check className="w-9 h-9 text-emerald-500" strokeWidth={2.5} />
      </div>
      <span className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 tracking-tight">Interested</span>
    </div>
  </motion.div>
);

/* ── Memoized Job Card — prevents re-renders of non-top cards ── */
const JobCard = React.memo(function JobCard({ job, onSwipe, isTop, exitX, onShowDetails }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 250], [-8, 8]);
  const likeOpacity = useTransform(x, [0, 60], [0, 1]);
  const nopeOpacity = useTransform(x, [-60, 0], [1, 0]);
  const bgScale = useTransform(x, [-150, 0, 150], [1, 0.95, 1]);
  const scale = isTop ? 1 : 0.95;

  const handleDragEnd = (_, info) => {
    const swipe = info.offset.x;
    const velocity = info.velocity.x;
    if (swipe > 60 || velocity > 400) onSwipe('right');
    else if (swipe < -60 || velocity < -400) onSwipe('left');
  };

  const openDetails = (e) => {
    e?.stopPropagation();
    onShowDetails?.(job);
  };

  const desc = (job.description || '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 500);

  const salary = (() => {
    if (job.salary && typeof job.salary === 'string') return job.salary;
    if (job.salary_min) return `$${Number(job.salary_min).toLocaleString()}${job.salary_max ? ` - $${Number(job.salary_max).toLocaleString()}` : '+'}`;
    if (job.salary_range) return job.salary_range;
    return '';
  })();

  const matchColor = !job.match_score ? ''
    : job.match_score >= 80 ? 'bg-emerald-50 text-emerald-700 ring-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-400'
    : job.match_score >= 60 ? 'bg-teal-50 text-teal-700 ring-teal-500/20 dark:bg-teal-500/15 dark:text-teal-400'
    : 'bg-amber-50 text-amber-700 ring-amber-500/20 dark:bg-amber-500/15 dark:text-amber-400';

  return (
    <motion.div
      className="absolute inset-0 will-change-transform"
      style={{ x: isTop ? x : 0, rotate: isTop ? rotate : 0, scale, zIndex: isTop ? 10 : 5 }}
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={isTop ? handleDragEnd : undefined}
      animate={isTop ? {} : { scale: 0.95, y: 10, opacity: 0.7 }}
      exit={{ x: exitX, opacity: 0, rotate: exitX > 0 ? 12 : -12, transition: { duration: 0.25 } }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      <div className="h-full bg-white dark:bg-[#1c1c1c] rounded-[28px] overflow-hidden flex flex-col border border-slate-100 dark:border-white/[0.08]"
        style={{ boxShadow: '0 12px 40px -10px rgba(0,0,0,0.08)' }}>

        {/* Top section */}
        <div className="p-4 sm:p-6 lg:p-8 pb-0 flex-shrink-0">
          <div className="flex items-start justify-between mb-3">
            {job.match_score != null && job.match_score > 0 ? (
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs sm:text-sm lg:text-base font-bold ring-1 shadow-sm ${matchColor}`}
                data-testid="match-score-badge">
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4" />
                {job.match_score}% match
              </div>
            ) : <div />}
            {job.source && (
              <span className="text-[10px] sm:text-xs lg:text-sm font-medium text-slate-400 dark:text-zinc-500 bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-full border border-slate-100 dark:border-white/[0.08]">
                {job.source}
              </span>
            )}
          </div>

          <h2
            className={`font-bold text-lg sm:text-2xl lg:text-3xl text-slate-900 dark:text-white tracking-tight leading-tight lg:leading-snug mb-2 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors`}
            onClick={openDetails}
            data-testid="swipe-job-title"
          >
            {job.job_title}
          </h2>
          <div
            className={`flex items-center gap-2 text-slate-600 dark:text-zinc-300 mb-3 sm:mb-4 lg:mb-5 cursor-pointer hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors`}
            onClick={openDetails}
          >
            <Building2 className="w-4 h-4 text-slate-400 dark:text-zinc-400" />
            <span className="text-sm sm:text-base lg:text-lg font-semibold">{job.company || 'Company'}</span>
          </div>
        </div>

        {/* Content section */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8 flex flex-col overflow-hidden">
          <div className="flex flex-wrap gap-2 sm:gap-2.5 mb-4 sm:mb-5">
            {job.location && job.location !== 'None' && (
              <span className="inline-flex items-center gap-1.5 bg-slate-50 dark:bg-white/[0.07] border border-slate-100 dark:border-white/[0.1] text-slate-700 dark:text-zinc-200 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-xl" data-testid="job-location-pill">
                <MapPin className="w-3.5 h-3.5 text-slate-400 dark:text-zinc-400" /> {job.location}
              </span>
            )}
            {job.is_remote && (
              <span className="inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-100 dark:border-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-xl">
                <Zap className="w-3.5 h-3.5" /> Remote
              </span>
            )}
            {job.employment_type && job.employment_type !== 'None' && (
              <span className="inline-flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-100 dark:border-indigo-500/25 text-indigo-700 dark:text-indigo-300 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-xl">
                <Briefcase className="w-3.5 h-3.5" /> {job.employment_type}
              </span>
            )}
            {salary && (
              <span className="inline-flex items-center gap-1.5 bg-amber-50 dark:bg-amber-500/15 border border-amber-100 dark:border-amber-500/25 text-amber-700 dark:text-amber-300 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-xl">
                <DollarSign className="w-3.5 h-3.5" /> {salary}
              </span>
            )}
          </div>

          <p
            className="text-slate-600 dark:text-zinc-300 text-sm lg:text-[15px] leading-relaxed lg:leading-7 flex-1 min-h-0 overflow-hidden cursor-pointer"
            onClick={openDetails}
          >
            {desc}{desc.length >= 500 ? '...' : ''}
          </p>

          {job.matched_skills && job.matched_skills.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {job.matched_skills.slice(0, 6).map((skill) => (
                <span key={`skill-${skill}`} className="bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-500/25 text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1.5 rounded-lg">
                  {skill}
                </span>
              ))}
              {job.matched_skills.length > 6 && (
                <span className="text-slate-400 dark:text-zinc-400 text-xs font-medium px-2 self-center">+{job.matched_skills.length - 6} more</span>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={openDetails}
            className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-sm lg:text-base font-semibold mt-4 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors self-start"
            data-testid="view-listing-link"
          >
            <ExternalLink className="w-4 h-4" /> View full description
          </button>
        </div>

        {/* Swipe overlays */}
        {isTop && (
          <>
            <motion.div className="absolute top-6 left-6 z-20 pointer-events-none"
              style={{ opacity: likeOpacity }}>
              <div className="bg-emerald-500/90 text-white px-5 py-2 rounded-xl text-lg font-black tracking-wider -rotate-12 shadow-lg">
                APPLY
              </div>
            </motion.div>
            <motion.div className="absolute top-6 right-6 z-20 pointer-events-none"
              style={{ opacity: nopeOpacity }}>
              <div className="bg-rose-500/90 text-white px-5 py-2 rounded-xl text-lg font-black tracking-wider rotate-12 shadow-lg">
                SKIP
              </div>
            </motion.div>
          </>
        )}
      </div>
    </motion.div>
  );
});

/* ── Processing Steps Bar ── */
const ProcessingStepsBar = ({ step, steps }) => (
  <motion.div
    initial={{ opacity: 0, y: -20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -20, scale: 0.95 }}
    className="mb-5 sm:mb-8 bg-gradient-to-br from-[#1a1a2e] to-[#16162a] rounded-3xl border border-indigo-500/20 px-5 sm:px-8 py-5 sm:py-6"
    style={{ boxShadow: '0 0 40px rgba(99,102,241,0.15), 0 12px 40px -10px rgba(0,0,0,0.3)' }}
    data-testid="processing-bar"
  >
    <div className="flex items-center gap-1 sm:gap-2 mb-4 sm:mb-5">
      {steps.map((s, i) => (
        <div key={`step-${s.label}`} className="flex items-center flex-1">
          <motion.div
            className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center text-base sm:text-lg lg:text-xl shrink-0 transition-all duration-300 ${
              i < step ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
              : i === step ? 'bg-indigo-500 text-white shadow-xl shadow-indigo-500/40 ring-4 ring-indigo-400/30'
              : 'bg-white/[0.06] text-zinc-500 border border-white/[0.1]'
            }`}
            animate={i === step ? { scale: [1, 1.12, 1], boxShadow: ['0 0 0px rgba(99,102,241,0.4)', '0 0 24px rgba(99,102,241,0.6)', '0 0 0px rgba(99,102,241,0.4)'] } : {}}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            {i < step ? <span className="text-lg sm:text-xl">&#10003;</span> : <span>{s.icon}</span>}
          </motion.div>
          {i < steps.length - 1 && (
            <div className="flex-1 h-1 sm:h-1.5 mx-2 sm:mx-3 rounded-full bg-white/[0.08] overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${i < step ? 'bg-emerald-400' : 'bg-indigo-500'}`}
                initial={{ width: i < step ? '100%' : '0%' }}
                animate={{ width: i < step ? '100%' : i === step ? '100%' : '0%' }}
                transition={{ duration: s.duration / 1000, ease: 'linear' }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
    <motion.p
      key={steps[step]?.label}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`text-base sm:text-lg lg:text-xl font-bold text-center tracking-tight ${steps[step]?.done ? 'text-emerald-400' : 'text-white'}`}
    >
      {steps[step]?.done && <span className="mr-2">&#10003;</span>}
      {steps[step]?.label}
    </motion.p>
    {!steps[step]?.done && (
      <div className="flex justify-center mt-2 gap-1">
        {[0,1,2].map(i => (
          <motion.div
            key={`dot-${i}`}
            className="w-1.5 h-1.5 rounded-full bg-indigo-400"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.2 }}
          />
        ))}
      </div>
    )}
  </motion.div>
);

/* ── Main Page ── */
export default function SwipeMode() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [liked, setLiked] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [processing, setProcessing] = useState(null);
  const [showPow, setShowPow] = useState(false);
  const [activeCvId, setActiveCvId] = useState(null);
  const [swipeStatus, setSwipeStatus] = useState(null);
  const [showBuySwipes, setShowBuySwipes] = useState(false);
  const [swipePackages, setSwipePackages] = useState({});
  const [purchasing, setPurchasing] = useState(false);
  const [exitX, setExitX] = useState(300);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [filterApplied, setFilterApplied] = useState(true);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [detailsJob, setDetailsJob] = useState(null);
  const [detailsFullDesc, setDetailsFullDesc] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);

  // ── Performance: refs for non-rendering state ──
  const swipeLock = useRef(false);
  const swipeQueue = useRef([]);     // Batch queue for right-swipe API calls
  const flushTimer = useRef(null);
  const fetchLock = useRef(false);   // Prevent duplicate fetches
  const headersRef = useRef(null);   // Cache auth headers
  const cvIdRef = useRef(null);      // Mirror of activeCvId for sync access

  // Keep refs in sync
  useEffect(() => { cvIdRef.current = activeCvId; }, [activeCvId]);
  useEffect(() => { headersRef.current = getAuthHeaders(); }, [getAuthHeaders]);

  // Fetch full job description when the details modal opens
  useEffect(() => {
    if (!detailsJob) {
      setDetailsFullDesc('');
      setDetailsLoading(false);
      return;
    }
    // Reset and start with whatever description we already have on the job
    const inlineDesc = (detailsJob.description || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    setDetailsFullDesc(inlineDesc);
    setDetailsLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/jobs/${detailsJob.id}/full-description`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const full = (res.data?.description || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        if (full && full.length > inlineDesc.length) setDetailsFullDesc(full);
      } catch (e) {
        // Keep inline description on error — non-fatal
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsJob]);

  const countries = [
    { code: '', label: 'All Countries' },
    { code: 'Spain', label: 'Spain' },
    { code: 'United States', label: 'United States' },
    { code: 'United Kingdom', label: 'United Kingdom' },
    { code: 'Germany', label: 'Germany' },
    { code: 'France', label: 'France' },
    { code: 'Netherlands', label: 'Netherlands' },
    { code: 'Italy', label: 'Italy' },
    { code: 'Portugal', label: 'Portugal' },
    { code: 'Ireland', label: 'Ireland' },
    { code: 'Sweden', label: 'Sweden' },
    { code: 'Switzerland', label: 'Switzerland' },
    { code: 'Canada', label: 'Canada' },
    { code: 'Australia', label: 'Australia' },
  ];

  const processingSteps = [
    { label: 'Personalizing your CV...', icon: '\u2728', duration: 1800 },
    { label: 'Crafting cover letter...', icon: '\u26A1', duration: 2000 },
    { label: 'Sending application...', icon: '\uD83D\uDCE8', duration: 1200 },
    { label: 'Application Sent!', icon: '\uD83D\uDE80', duration: 1200, done: true },
  ];

  useEffect(() => {
    fetchCv();
    fetchSwipeStatus();
    fetchJobs(1);
    try { track(EVENTS.SWIPE_MODE_STARTED); } catch (_) {}
    return () => { if (flushTimer.current) clearTimeout(flushTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSwipeStatus = async () => {
    try {
      const res = await axios.get(`${API}/swipes/status`, { headers: getAuthHeaders() });
      setSwipeStatus(res.data);
    } catch (e) { console.error("Swipe fetch error:", e); }
  };

  const fetchCv = async () => {
    try {
      const res = await axios.get(`${API}/cv/list`, { headers: getAuthHeaders() });
      const cvs = res.data || [];
      if (cvs.length > 0) {
        setActiveCvId(cvs[0].id);
        cvIdRef.current = cvs[0].id;
        const pc = cvs[0].parsed_content;
        if (pc) {
          const city = (pc.city || '').trim();
          const country = (pc.country || '').trim();
          if (country) setFilterCountry(country);
          if (city) setFilterCity(city);
        }
      }
    } catch (e) { console.error("CV fetch error:", e); }
  };

  const fetchJobs = useCallback(async (pg = 1) => {
    if (fetchLock.current && pg !== 1) return;
    fetchLock.current = true;
    try {
      setLoading(true);
      let newJobs = [];
      const params = new URLSearchParams({ limit: '50' });
      if (filterCountry) params.append('country', filterCountry);
      if (filterCity) params.append('city', filterCity);

      const useCache = pg === 1;
      const headers = getAuthHeaders();

      try {
        const recUrl = `${API}/recommendations/jobs?${params}`;
        let recData;
        if (useCache) {
          const r = await cachedGet(recUrl, { headers }, SWIPE_CACHE_TTL_MS);
          recData = r.data;
        } else {
          recData = (await axios.get(recUrl, { headers })).data;
        }
        newJobs = (recData || []).sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
      } catch (e) {
        console.error("Recommendations fetch error:", e);
      }

      if (newJobs.length === 0) {
        try {
          const feedParams = new URLSearchParams({ limit: '50' });
          if (filterCountry) feedParams.append('location', filterCountry);
          const feedUrl = `${API}/jobs/feed-scored?${feedParams}`;
          let feedData;
          if (useCache) {
            const r = await cachedGet(feedUrl, { headers }, SWIPE_CACHE_TTL_MS);
            feedData = r.data;
          } else {
            feedData = (await axios.get(feedUrl, { headers })).data;
          }
          newJobs = (feedData || []).sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
        } catch (e) {
          console.error("Feed fallback error:", e);
        }
      }
      
      if (pg === 1) { setJobs(newJobs); setCurrentIdx(0); }
      else { 
        setJobs(prev => {
          const existingIds = new Set(prev.map(j => j.id));
          const unique = newJobs.filter(j => !existingIds.has(j.id));
          const combined = [...prev, ...unique];
          return combined.length > 100 ? combined.slice(combined.length - 100) : combined;
        }); 
      }
      setPage(pg);
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
      fetchLock.current = false;
    }
  }, [filterCountry, filterCity, getAuthHeaders]);

  const fetchSwipePackages = async () => {
    try {
      const res = await axios.get(`${API}/swipes/packages`, { headers: getAuthHeaders() });
      setSwipePackages(res.data || {});
    } catch (e) { console.error("Packages fetch error:", e); }
  };

  const handleBuySwipes = async (packageId) => {
    try {
      setPurchasing(true);
      const origin = window.location.origin;
      const res = await axios.post(`${API}/swipes/buy`, { package_id: packageId, origin_url: origin }, { headers: getAuthHeaders() });
      if (res.data.checkout_url) {
        try { track(EVENTS.SWIPES_PURCHASED, { package: packageId }); } catch (_) {}
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
    } finally { setPurchasing(false); }
  };

  const handleApplyFilter = () => {
    invalidateCache('/recommendations/jobs');
    invalidateCache('/jobs/feed-scored');
    setFilterApplied(true);
    setJobs([]);
    setCurrentIdx(0);
    setLiked([]);
    setSkipped([]);
    setHistory([]);
    fetchJobs(1);
  };

  const detectCurrentLocation = async () => {
    setDetectingLocation(true);
    try {
      const res = await fetch('https://ipapi.co/json/');
      const data = await res.json();
      if (data.country_name) setFilterCountry(data.country_name);
      if (data.city) setFilterCity(data.city);
      toast.success(`Detected: ${data.city || ''}, ${data.country_name || ''}`);
    } catch (e) {
      console.error("Location detection error:", e);
      toast.error('Could not detect location. Please select manually.');
    } finally {
      setDetectingLocation(false);
    }
  };

  // ── Batched API flush: sends queued right-swipe calls every 2s ──
  const flushSwipeQueue = useCallback(() => {
    const queue = [...swipeQueue.current];
    swipeQueue.current = [];
    if (queue.length === 0) return;

    const headers = headersRef.current;
    const cvId = cvIdRef.current;
    if (!headers || !cvId) return;

    // Batch swipe count
    axios.post(`${API}/swipes/batch`, { count: queue.length }, { headers }).catch(() => {});

    // Fire application creates in parallel (non-blocking)
    for (const job of queue) {
      axios.post(`${API}/applications/interested`, { cv_id: cvId, job_id: job.id }, { headers })
        .catch(err => {
          const msg = err.response?.data?.detail || '';
          if (!msg.includes('already')) {
            // Silently log — don't toast during rapid swiping
          }
        });
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushSwipeQueue, 2000);
  }, [flushSwipeQueue]);

  // ── Core swipe handler — zero async, instant UI ──
  const handleSwipe = useCallback((direction) => {
    if (swipeLock.current) return;

    // Read directly from state updater to avoid stale closure
    setJobs(prevJobs => {
      setCurrentIdx(prevIdx => {
        const job = prevJobs[prevIdx];
        if (!job) return prevIdx;

        // Lock for 80ms to prevent double-swipes
        swipeLock.current = true;
        setTimeout(() => { swipeLock.current = false; }, 80);

        setExitX(direction === 'right' ? 300 : -300);

        if (direction === 'right') {
          const cvId = cvIdRef.current;
          if (!cvId) {
            // Async CV fetch — don't block the swipe
            axios.get(`${API}/cv/list`, { headers: headersRef.current }).then(res => {
              const cvs = res.data || [];
              if (cvs.length > 0) {
                setActiveCvId(cvs[0].id);
                cvIdRef.current = cvs[0].id;
              } else {
                toast.error('Please upload a CV first', { action: { label: 'Upload CV', onClick: () => { window.location.href = '/cv'; } } });
              }
            }).catch(() => {});
            return prevIdx; // Don't advance if no CV
          }

          // Queue for batch API call
          swipeQueue.current.push({ id: job.id, job_title: job.job_title, company: job.company });
          scheduleFlush();
          try { track(EVENTS.SWIPE_RIGHT, { job_id: job.id }); } catch (_) {}

          // Optimistic local updates
          setSwipeStatus(prev => prev ? {
            ...prev,
            swipes_used: prev.swipes_used + 1,
            plan_remaining: prev.is_unlimited ? -1 : Math.max(0, (prev.plan_remaining || 0) - 1),
            total_remaining: prev.is_unlimited ? -1 : Math.max(0, (prev.total_remaining || 0) - 1),
          } : prev);

          setShowPow(true);
          setTimeout(() => setShowPow(false), 1200);

          // Show processing animation
          setProcessing({ job: { id: job.id, job_title: job.job_title, company: job.company }, step: 0 });
          let step = 0;
          const advance = () => {
            step++;
            if (step < 4) { // processingSteps.length
              setProcessing(p => p ? { ...p, step } : null);
              setTimeout(advance, [1800, 2000, 1200, 1200][step]);
            } else {
              setProcessing(null);
              setLiked(prev => {
                const updated = [...prev, { id: job.id, job_title: job.job_title, company: job.company }];
                return updated.length > 50 ? updated.slice(-50) : updated;
              });
            }
          };
          setTimeout(advance, 1800);

          setHistory(prev => {
            const updated = [...prev, { job: { id: job.id, job_title: job.job_title, company: job.company }, direction: 'right', index: prevIdx }];
            return updated.length > 30 ? updated.slice(-30) : updated;
          });
        } else {
          // Left swipe — pure local, zero network
          try { track(EVENTS.SWIPE_LEFT, { job_id: job.id }); } catch (_) {}
          setSkipped(prev => {
            const updated = [...prev, { id: job.id }];
            return updated.length > 30 ? updated.slice(-30) : updated;
          });
          setHistory(prev => {
            const updated = [...prev, { job: { id: job.id, job_title: job.job_title, company: job.company }, direction: 'left', index: prevIdx }];
            return updated.length > 30 ? updated.slice(-30) : updated;
          });
        }

        const next = prevIdx + 1;
        // Prefetch more jobs when running low
        if (prevJobs.length - next <= 8) {
          setPage(p => { fetchJobs(p + 1); return p + 1; });
        }
        // Trim consumed jobs to prevent memory bloat
        if (next > 25) {
          setJobs(pj => pj.slice(next));
          return 0;
        }
        return next;
      });
      return prevJobs; // Don't modify jobs array here
    });
  }, [fetchJobs, scheduleFlush]);

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const lastEntry = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    if (lastEntry.direction === 'left') {
      setSkipped(prev => prev.filter(s => s.id !== lastEntry.job.id));
    } else {
      setLiked(prev => prev.filter(l => l.id !== lastEntry.job.id));
    }
    setCurrentIdx(prev => Math.max(0, prev - 1));
  }, [history]);

  // ── Derived state ──
  const currentJob = jobs[currentIdx];
  const nextJob = jobs[currentIdx + 1];
  const allDone = !loading && jobs.length > 0 && currentIdx >= jobs.length;
  const visibleJobs = useMemo(() => {
    const result = [];
    if (nextJob) result.push(nextJob);
    if (currentJob) result.push(currentJob);
    return result;
  }, [currentJob, nextJob]);

  // ── Flush queue on unmount ──
  useEffect(() => {
    return () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushSwipeQueue(); // Final flush
    };
  }, [flushSwipeQueue]);

  return (
    <AppLayout>
      <div className="min-h-[calc(100vh-60px)] bg-gradient-to-b from-slate-50 to-slate-100 dark:from-[#0f0f0f] dark:to-[#0a0a0a] overflow-x-hidden">
        <div className="relative z-10 px-4 lg:px-8">
          {/* Header */}
          <div className="text-center pt-4 mb-4 lg:mb-6">
            <h1 className="font-bold text-3xl sm:text-4xl text-white tracking-tight">Swipe Mode</h1>
            <p className="text-zinc-400 mt-2 text-sm">Swipe right if you're interested, left to skip</p>
          </div>

          {/* Stats bar + filter toggle */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-4 max-w-3xl mx-auto">
            <div className="flex items-center gap-3 bg-white dark:bg-[#1c1c1c] border border-slate-100 dark:border-white/[0.08] rounded-full px-4 py-2 shadow-sm">
              <div className="flex items-center gap-1">
                <Heart className="w-4 h-4 text-rose-400" />
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">{liked.length}</span>
              </div>
              <div className="w-px h-4 bg-slate-200 dark:bg-white/10" />
              <div className="flex items-center gap-1">
                <X className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-700 dark:text-zinc-300">{skipped.length}</span>
              </div>
              {swipeStatus && !swipeStatus.is_unlimited && (
                <>
                  <div className="w-px h-4 bg-slate-200 dark:bg-white/10" />
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{swipeStatus.total_remaining} left</span>
                </>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilter(!showFilter)}
              className="rounded-full text-xs h-9 px-4 dark:border-white/[0.1] dark:text-zinc-300"
              data-testid="filter-toggle-btn"
            >
              <MapPin className="w-3.5 h-3.5 mr-1.5" />
              {filterCountry || 'Filter'}
            </Button>
          </div>

          {/* Collapsible filter panel */}
          <AnimatePresence>
          {showFilter && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-4 max-w-3xl mx-auto"
            >
              <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-slate-100 dark:border-white/[0.08] p-4 shadow-sm mb-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-500 dark:text-zinc-400 mb-1 block">Country</label>
                    <select
                      value={filterCountry}
                      onChange={e => { setFilterCountry(e.target.value); setFilterApplied(false); }}
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/[0.1] rounded-xl px-3 py-2.5 text-sm text-slate-700 dark:text-zinc-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                      data-testid="filter-country-select"
                    >
                      {countries.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-500 dark:text-zinc-400 mb-1 block">City <span className="text-slate-400 dark:text-zinc-500">(optional)</span></label>
                    <input
                      type="text"
                      value={filterCity}
                      onChange={e => { setFilterCity(e.target.value); setFilterApplied(false); }}
                      placeholder="Any city"
                      className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/[0.1] rounded-xl px-3 py-2.5 text-sm text-slate-700 dark:text-zinc-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                      data-testid="filter-city-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={detectCurrentLocation}
                      variant="outline"
                      disabled={detectingLocation}
                      className="rounded-xl h-[42px] px-3 text-xs dark:border-white/[0.1] dark:text-zinc-300 whitespace-nowrap"
                      data-testid="detect-location-btn"
                    >
                      {detectingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={handleApplyFilter}
                      disabled={filterApplied}
                      className="rounded-xl h-[42px] bg-indigo-500 hover:bg-indigo-600 text-white text-xs px-5"
                      data-testid="apply-filter-btn"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          </AnimatePresence>

          {/* Processing steps */}
          <AnimatePresence>
            {processing && (
              <div className="max-w-md lg:max-w-2xl mx-auto">
                <ProcessingStepsBar step={processing.step} steps={processingSteps} />
              </div>
            )}
          </AnimatePresence>

          {/* Single card layout */}
          <div className="max-w-md lg:max-w-2xl mx-auto w-full">
              {/* Card stack */}
              <div className="relative w-full" style={{ height: 'min(600px, calc(100vh - 320px))' }}>
                {loading && jobs.length === 0 ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3 text-center">
                      <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                      <p className="text-slate-500 dark:text-zinc-400 text-sm">Finding your best matches...</p>
                    </div>
                  </div>
                ) : allDone ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center px-6 py-10 bg-white dark:bg-[#1c1c1c] rounded-3xl border border-slate-100 dark:border-white/[0.08] shadow-lg" data-testid="all-done-card">
                      <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 dark:bg-emerald-500/15 rounded-full flex items-center justify-center">
                        <Check className="w-10 h-10 text-emerald-500" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">All caught up!</h3>
                      <p className="text-sm text-slate-500 dark:text-zinc-400 mb-6">You've gone through all available matches</p>
                      <Button onClick={() => { invalidateCache('/recommendations/jobs'); invalidateCache('/jobs/feed-scored'); setJobs([]); setCurrentIdx(0); fetchJobs(1); }}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full px-8 py-5" data-testid="refresh-jobs-btn">
                        <Loader2 className="w-4 h-4 mr-2" /> Load More Jobs
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <AnimatePresence mode="popLayout">
                      {visibleJobs.map((job, i) => {
                        const isTop = job.id === currentJob?.id;
                        return (
                          <JobCard
                            key={job.id}
                            job={job}
                            onSwipe={handleSwipe}
                            isTop={isTop}
                            exitX={exitX}
                            onShowDetails={setDetailsJob}
                          />
                        );
                      })}
                    </AnimatePresence>
                    {showPow && <AppliedPopup />}
                  </>
                )}
              </div>

          {/* Action buttons */}
          {!allDone && currentJob && (
            <div className="flex items-center justify-center gap-3 sm:gap-5 mt-6 sm:mt-8 mb-6 sm:mb-8 pb-4">
              <button
                onClick={handleUndo}
                disabled={history.length === 0}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white dark:bg-[#252525] text-slate-400 dark:text-zinc-500 shadow-md border border-slate-100 dark:border-white/[0.08] flex items-center justify-center disabled:opacity-30 hover:scale-105 active:scale-95 transition-all"
                data-testid="swipe-undo-btn"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleSwipe('left')}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white dark:bg-[#252525] text-rose-500 shadow-xl border border-slate-100 dark:border-white/[0.08] flex items-center justify-center hover:scale-110 active:scale-95 transition-all"
                data-testid="swipe-skip-btn"
              >
                <X className="w-8 h-8" strokeWidth={3} />
              </button>
              <button
                onClick={() => handleSwipe('right')}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center hover:scale-110 active:scale-95 transition-all"
                style={{ boxShadow: '0 20px 40px rgba(99,102,241,0.35)' }}
                data-testid="swipe-apply-btn"
              >
                <Heart className="w-9 h-9" strokeWidth={2.5} />
              </button>
              <button
                onClick={() => currentJob && setDetailsJob(currentJob)}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white dark:bg-[#252525] text-indigo-500 dark:text-indigo-400 shadow-md border border-slate-100 dark:border-white/[0.08] flex items-center justify-center hover:scale-105 active:scale-95 transition-all"
                data-testid="swipe-view-btn"
                title="View full description"
              >
                <ExternalLink className="w-5 h-5" />
              </button>
            </div>
          )}
            </div>{/* end single card container */}

          {/* Liked jobs list */}
          {liked.length > 0 && (
            <div className="mt-6 pb-10 max-w-3xl mx-auto">
              <h3 className="text-sm font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-3 px-1" data-testid="liked-section-title">
                Interested ({liked.length}) · <button onClick={() => navigate('/applications')} className="normal-case tracking-normal text-indigo-500 hover:underline" data-testid="liked-go-tailor">tailor & apply →</button>
              </h3>
              <div className="space-y-2">
                {liked.slice(-5).reverse().map((job) => (
                  <div key={`liked-${job.id}`}
                    className="flex items-center gap-3 bg-white dark:bg-[#1c1c1c] rounded-xl border border-slate-100 dark:border-white/[0.08] px-4 py-3 shadow-sm"
                    data-testid={`liked-job-${job.id}`}>
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{job.job_title}</p>
                      <p className="text-xs text-slate-400 dark:text-zinc-500">{job.company}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Job details modal — opens when user taps the title/company/description in SwipeMode */}
      <AnimatePresence>
        {detailsJob && (() => {
          const dj = detailsJob;
          const djSalary = (() => {
            if (dj.salary && typeof dj.salary === 'string') return dj.salary;
            if (dj.salary_min) return `$${Number(dj.salary_min).toLocaleString()}${dj.salary_max ? ` - $${Number(dj.salary_max).toLocaleString()}` : '+'}`;
            if (dj.salary_range) return dj.salary_range;
            return '';
          })();
          const djMatchColor = !dj.match_score ? ''
            : dj.match_score >= 80 ? 'bg-emerald-50 text-emerald-700 ring-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-400'
            : dj.match_score >= 60 ? 'bg-teal-50 text-teal-700 ring-teal-500/20 dark:bg-teal-500/15 dark:text-teal-400'
            : 'bg-amber-50 text-amber-700 ring-amber-500/20 dark:bg-amber-500/15 dark:text-amber-400';
          return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setDetailsJob(null)}
            data-testid="job-details-modal"
          >
            <motion.div
              initial={{ y: 40, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 40, scale: 0.98, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 360, damping: 32 }}
              className="bg-white dark:bg-[#1c1c1c] w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl border border-slate-100 dark:border-white/[0.08] shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-5 sm:px-7 pt-5 sm:pt-6 pb-3 border-b border-slate-100 dark:border-white/[0.06] shrink-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {dj.match_score != null && dj.match_score > 0 && (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ring-1 ${djMatchColor}`}>
                        <Sparkles className="w-3 h-3" />
                        {dj.match_score}% match
                      </span>
                    )}
                    {dj.source && (
                      <span className="text-[10px] sm:text-xs font-medium text-slate-400 dark:text-zinc-500 bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-full border border-slate-100 dark:border-white/[0.08]">
                        {dj.source}
                      </span>
                    )}
                  </div>
                  <h2 className="font-bold text-lg sm:text-2xl text-slate-900 dark:text-white tracking-tight leading-tight" data-testid="job-details-title">
                    {dj.job_title}
                  </h2>
                  <div className="flex items-center gap-2 text-slate-600 dark:text-zinc-300 mt-1.5">
                    <Building2 className="w-4 h-4 text-slate-400 dark:text-zinc-400" />
                    <span className="text-sm sm:text-base font-semibold">{dj.company || 'Company'}</span>
                  </div>
                </div>
                <button
                  onClick={() => setDetailsJob(null)}
                  className="w-9 h-9 rounded-full bg-slate-100 dark:bg-white/[0.06] hover:bg-slate-200 dark:hover:bg-white/[0.1] text-slate-500 dark:text-zinc-400 flex items-center justify-center shrink-0 transition-colors"
                  data-testid="job-details-close-btn"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-4 sm:py-5">
                {/* Pills */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {dj.location && dj.location !== 'None' && (
                    <span className="inline-flex items-center gap-1.5 bg-slate-50 dark:bg-white/[0.07] border border-slate-100 dark:border-white/[0.1] text-slate-700 dark:text-zinc-200 text-xs font-semibold px-3 py-1.5 rounded-xl">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 dark:text-zinc-400" /> {dj.location}
                    </span>
                  )}
                  {dj.is_remote && (
                    <span className="inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-100 dark:border-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1.5 rounded-xl">
                      <Zap className="w-3.5 h-3.5" /> Remote
                    </span>
                  )}
                  {dj.employment_type && dj.employment_type !== 'None' && (
                    <span className="inline-flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-100 dark:border-indigo-500/25 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-3 py-1.5 rounded-xl">
                      <Briefcase className="w-3.5 h-3.5" /> {dj.employment_type}
                    </span>
                  )}
                  {djSalary && (
                    <span className="inline-flex items-center gap-1.5 bg-amber-50 dark:bg-amber-500/15 border border-amber-100 dark:border-amber-500/25 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-xl">
                      <DollarSign className="w-3.5 h-3.5" /> {djSalary}
                    </span>
                  )}
                </div>

                {/* Matched skills */}
                {dj.matched_skills && dj.matched_skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {dj.matched_skills.map((skill) => (
                      <span key={`dj-skill-${skill}`} className="bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-500/25 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-lg">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                {/* Description body */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold text-slate-400 dark:text-zinc-500 uppercase tracking-wider">Job description</h3>
                    {detailsLoading && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-zinc-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading full text…
                      </span>
                    )}
                  </div>
                  <p className="text-slate-700 dark:text-zinc-200 text-sm sm:text-[15px] leading-relaxed whitespace-pre-line" data-testid="job-details-description">
                    {detailsFullDesc || 'No description available for this job.'}
                  </p>
                </div>

                {dj.url && (
                  <a
                    href={dj.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                    data-testid="job-details-original-link"
                  >
                    <ExternalLink className="w-4 h-4" /> Open original posting
                  </a>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex items-center gap-3 px-5 sm:px-7 py-4 border-t border-slate-100 dark:border-white/[0.06] bg-slate-50/60 dark:bg-white/[0.02] shrink-0">
                <button
                  onClick={() => { setDetailsJob(null); handleSwipe('left'); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-rose-600 dark:text-rose-400 bg-white dark:bg-white/[0.04] border border-rose-200 dark:border-rose-500/25 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                  data-testid="job-details-skip-btn"
                >
                  <X className="w-4 h-4" /> Skip
                </button>
                <button
                  onClick={() => { setDetailsJob(null); handleSwipe('right'); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-white bg-gradient-to-tr from-indigo-600 to-purple-600 hover:opacity-95 transition-opacity shadow-lg shadow-indigo-500/25"
                  data-testid="job-details-apply-btn"
                >
                  <Heart className="w-4 h-4" /> Interested
                </button>
              </div>
            </motion.div>
          </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Buy swipes modal */}
      <AnimatePresence>
        {showBuySwipes && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowBuySwipes(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white dark:bg-[#1c1c1c] rounded-3xl border border-slate-200 dark:border-white/[0.1] p-6 max-w-sm w-full shadow-2xl"
              onClick={e => e.stopPropagation()}>
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto mb-3 bg-indigo-100 dark:bg-indigo-500/15 rounded-full flex items-center justify-center">
                  <Heart className="w-7 h-7 text-indigo-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Need More Swipes?</h3>
                <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">Get extra swipes to keep discovering jobs</p>
              </div>
              <div className="space-y-2">
                {Object.entries(swipePackages).map(([id, pkg]) => (
                  <button key={id}
                    onClick={() => handleBuySwipes(id)}
                    disabled={purchasing}
                    className="w-full flex items-center justify-between bg-slate-50 dark:bg-white/5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 border border-slate-200 dark:border-white/[0.08] rounded-xl px-4 py-3 transition-colors"
                    data-testid={`buy-swipe-${id}`}>
                    <div className="flex items-center gap-3">
                      <ShoppingCart className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-semibold text-slate-700 dark:text-zinc-200">{pkg.swipes} Swipes</span>
                    </div>
                    <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{'\u20AC'}{pkg.price}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => setShowBuySwipes(false)}
                className="w-full mt-4 text-sm text-slate-400 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 transition-colors"
                data-testid="close-buy-swipes">
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
